import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChevronRight, ChevronLeft, Check,
  Lightbulb, Rocket, TrendingUp, Building, Palette, RefreshCw, Globe, Megaphone,
  Monitor, BarChart, Zap, Clock, Calendar, CalendarRange, Search,
  DollarSign, Banknote, Gem, Crown, Target, Users, Award, Sparkles
} from 'lucide-react';
import { quizVariants, quizResults, calculateQuizResult, getAssignedVariant } from '../quizData';

const iconMap = {
  Lightbulb, Rocket, TrendingUp, Building, Palette, RefreshCw, Globe, Megaphone,
  Monitor, BarChart, Zap, Clock, Calendar, CalendarRange, Search,
  DollarSign, Banknote, Gem, Crown, Target, Users, Award
};

const BrandQuiz = ({ isOpen, onClose, onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [result, setResult] = useState(null);

  // Sticky A/B assignment — decided once per browser, persisted in localStorage
  const variant = useMemo(() => getAssignedVariant(), []);
  const quizQuestions = quizVariants[variant] || quizVariants.A;

  // Record a quiz-start event the first time the modal opens per session.
  // Pairs with the submit call in the backend to compute real completion rate.
  useEffect(() => {
    if (!isOpen) return;
    const sessionId =
      sessionStorage.getItem('sessionId') ||
      `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('sessionId', sessionId);
    // Only fire once per session — avoids inflating starts if user reopens
    const alreadyStarted = sessionStorage.getItem('icd_quiz_started') === '1';
    if (alreadyStarted) return;
    sessionStorage.setItem('icd_quiz_started', '1');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quiz/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId, variant }),
    }).catch(() => {}); // silent — tracking must never break the UX
  }, [isOpen, variant]);

  const handleAnswer = (questionId, value) => {
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);
    
    // Auto-advance after selection
    setTimeout(async () => {
      if (currentQuestion < quizQuestions.length - 1) {
        setCurrentQuestion(prev => prev + 1);
      } else {
        // Calculate and show results
        const resultKey = calculateQuizResult(newAnswers);
        const quizResult = quizResults[resultKey] || quizResults.default;
        
        // Store quiz submission in backend
        try {
          const sessionId = sessionStorage.getItem('sessionId') || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          sessionStorage.setItem('sessionId', sessionId);
          
          const quizData = {
            session_id: sessionId,
            variant,
            answers: newAnswers,
            result_key: resultKey,
            result_title: quizResult.title,
            recommended_services: quizResult.recommendedServices,
            investment_range: quizResult.investment,
            timeline: quizResult.timeline
          };

          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quiz/submit`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(quizData)
          });

          if (response.ok) {
            const submissionResult = await response.json();
            console.log('Quiz submitted successfully:', submissionResult);
          }
        } catch (error) {
          console.error('Error submitting quiz:', error);
        }
        
        setResult(quizResult);
        setShowResults(true);
      }
    }, 300);
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleRestart = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setResult(null);
  };

  const handleBookConsultation = () => {
    onComplete(result);
    onClose();
    // Scroll to contact form
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const question = quizQuestions[currentQuestion];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl border border-cyan-500/30 shadow-2xl"
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-6 right-6 z-10 w-10 h-10 bg-slate-800/50 border border-cyan-500/30 rounded-full flex items-center justify-center text-gray-400 hover:text-cyan-400 hover:border-cyan-500 transition-all"
          >
            <X size={20} />
          </button>

          {!showResults ? (
            <>
              {/* Progress Bar */}
              <div className="p-8 pb-0">
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-cyan-400 font-semibold">
                      Question {currentQuestion + 1} of {quizQuestions.length}
                    </span>
                    <span className="text-sm text-gray-400 flex items-center gap-2 pr-12">
                      <span className="hidden sm:inline-block px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest bg-slate-800/80 border border-cyan-500/20 rounded text-cyan-400/70" title={`A/B test variant ${variant}`}>
                        v{variant}
                      </span>
                      {Math.round(progress)}% Complete
                    </span>
                  </div>
                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      className="h-full bg-gradient-to-r from-cyan-500 to-blue-600 neon-glow"
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                </div>
              </div>

              {/* Question */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentQuestion}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="p-8"
                >
                  <div className="text-center mb-8">
                    <h2 className="text-3xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600 mb-3">
                      {question.question}
                    </h2>
                    <p className="text-gray-400 text-lg">
                      {question.subtitle}
                    </p>
                  </div>

                  {/* Options */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {question.options.map((option) => {
                      const Icon = iconMap[option.icon];
                      const isSelected = answers[question.id] === option.value;
                      
                      return (
                        <motion.button
                          key={option.id}
                          whileHover={{ scale: 1.02, y: -2 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleAnswer(question.id, option.value)}
                          className={`relative p-4 sm:p-6 rounded-2xl border-2 text-left transition-all ${
                            isSelected
                              ? 'border-cyan-500 bg-cyan-500/10'
                              : 'border-cyan-500/20 bg-slate-800/50 hover:border-cyan-500/50'
                          }`}
                        >
                          <div className="flex items-start gap-3 sm:gap-4">
                            <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center ${
                              isSelected
                                ? 'bg-gradient-to-br from-cyan-500 to-blue-600 neon-glow'
                                : 'bg-slate-700'
                            }`}>
                              <Icon size={20} className="sm:w-6 sm:h-6 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-white font-semibold text-base sm:text-lg">
                                {option.text}
                              </div>
                            </div>
                            {isSelected && (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="flex-shrink-0"
                              >
                                <Check size={20} className="text-cyan-400" />
                              </motion.div>
                            )}
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Navigation */}
              <div className="p-8 pt-0 flex justify-between items-center">
                <button
                  onClick={handlePrevious}
                  disabled={currentQuestion === 0}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                    currentQuestion === 0
                      ? 'text-gray-600 cursor-not-allowed'
                      : 'text-cyan-400 hover:bg-cyan-500/10'
                  }`}
                >
                  <ChevronLeft size={20} />
                  Previous
                </button>
                
                <div className="flex gap-2">
                  {quizQuestions.map((_, index) => (
                    <div
                      key={index}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentQuestion
                          ? 'w-8 bg-cyan-500'
                          : answers[index + 1]
                          ? 'bg-cyan-500/50'
                          : 'bg-gray-600'
                      }`}
                    />
                  ))}
                </div>

                <div className="w-32"></div>
              </div>
            </>
          ) : (
            /* Results */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-8 sm:p-12"
            >
              <div className="text-center mb-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring" }}
                  className="inline-block mb-6"
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center neon-glow-strong">
                    <Sparkles size={40} className="text-white" />
                  </div>
                </motion.div>

                <h2 className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600 mb-3">
                  {result?.title}
                </h2>
                <p className="text-xl text-cyan-400 font-semibold mb-6">
                  {result?.subtitle}
                </p>
                <p className="text-lg text-gray-300 leading-relaxed max-w-2xl mx-auto">
                  {result?.description}
                </p>
              </div>

              {/* Recommended Services */}
              <div className="mb-6 sm:mb-8">
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4 text-center px-2">
                  Recommended Services
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {result?.recommendedServices.map((service, index) => (
                    <motion.div
                      key={service}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                      className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-slate-800/50 border border-cyan-500/20 rounded-xl"
                    >
                      <Check size={18} className="sm:w-5 sm:h-5 text-cyan-400 flex-shrink-0" />
                      <span className="text-white font-semibold text-sm sm:text-base">{service}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Investment & Timeline */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div className="p-4 sm:p-6 bg-gradient-to-br from-slate-800/80 to-slate-700/80 rounded-2xl border border-cyan-500/20">
                  <div className="text-gray-400 text-xs sm:text-sm mb-2">Investment Range</div>
                  <div className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                    {result?.investment}
                  </div>
                </div>
                <div className="p-4 sm:p-6 bg-gradient-to-br from-slate-800/80 to-slate-700/80 rounded-2xl border border-purple-500/20">
                  <div className="text-gray-400 text-xs sm:text-sm mb-2">Estimated Timeline</div>
                  <div className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">
                    {result?.timeline}
                  </div>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-2">
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: '0 0 40px rgba(0,245,255,0.8)' }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBookConsultation}
                  className="w-full sm:w-auto px-6 py-3 sm:px-8 sm:py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-base sm:text-lg font-bold rounded-lg neon-glow-strong flex items-center justify-center gap-2"
                >
                  <Sparkles size={18} className="flex-shrink-0" />
                  <span>{result?.nextStep}</span>
                </motion.button>
                <button
                  onClick={handleRestart}
                  className="w-full sm:w-auto px-6 py-3 sm:px-8 sm:py-4 bg-transparent border-2 border-cyan-500 text-cyan-400 text-base sm:text-lg font-bold rounded-lg hover:bg-cyan-500/10 transition-all"
                >
                  Retake Quiz
                </button>
              </div>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BrandQuiz;
